import type { NextApiRequest, NextApiResponse } from 'next';
import { cloneDeep, difference } from 'lodash';
import {
  type ProvidedServiceTrades,
  getFiltersLists,
} from '../../services/business-registry';
import { providedServices } from '../../data/filters/provided-services';
import { init as initializeBugsnag } from '../../lib/bugsnag/bugsnag';

initializeBugsnag();

let tradesFromBRCache;

const getWithSynonyms = (tradesFromBR) => {
  if (tradesFromBRCache) {
    return tradesFromBRCache;
  }
  const trades = cloneDeep(tradesFromBR);
  let specialties = [];
  trades.forEach((trade) => {
    specialties = specialties.concat(trade.specialties ?? []);
  });
  tradesFromBRCache = trades.concat(specialties).map((trade) => {
    if (providedServices[trade.key]) {
      return {
        ...providedServices[trade.key],
        ...trade,
      };
    }
    return {
      ...trade,
    };
  });
  return tradesFromBRCache;
};

const MAX_FILTER_INPUTS = 500;

const toSafeArray = <T>(value: unknown, maxLength = MAX_FILTER_INPUTS): T[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.slice(0, maxLength) as T[];
};

const findMatches = (
  list,
  inputs,
  additionalLabelMatch: Function = () => true
) => {
  const safeInputs = toSafeArray<string>(inputs);
  const safeList = Array.isArray(list) ? list : [];
  const inputsWithExactMatch = [];
  const filtersWithExactMatch = safeList.filter((item) => {
    return safeInputs.some((input) => {
      const newInput = input.toLowerCase().trim();
      const isMatch =
        item.key.toLowerCase() === newInput ||
        item.urlKey.toLowerCase() === newInput ||
        item.label.toLowerCase() === newInput;
      if (isMatch) {
        inputsWithExactMatch.push(input);
      }
      return isMatch;
    });
  });
  const diffFilters = difference(safeList, filtersWithExactMatch) as any;
  const diffKeys = difference(safeInputs, inputsWithExactMatch);
  const fuzzyMatches = diffFilters.filter((item) => {
    return diffKeys.some((input) => {
      const newInput = input.toLowerCase().trim();
      return (
        item.synonyms?.some((s) => s.toLowerCase() === newInput) ||
        (additionalLabelMatch(item) &&
          item.label.toLowerCase().indexOf(newInput) > -1 &&
          newInput.length > 0)
      );
    });
  });
  return [...filtersWithExactMatch, ...fuzzyMatches];
};

const findTrades = async (
  tradesFromBR: Array<ProvidedServiceTrades>,
  inputs: Array<string>
): Promise<Array<ProvidedServiceTrades>> => {
  if (!inputs || !inputs.length) {
    return undefined;
  }
  const trades = getWithSynonyms(tradesFromBR);
  const matches = findMatches(trades, inputs, (item) => item.level === 'trade');

  matches.forEach((match) => {
    if (match.level === 'trade') {
      const missingSpecialties = trades
        .filter((t) => t.tradeKey === match.key)
        .filter((s) => !matches.includes(s));
      matches.push(...missingSpecialties);
    } else {
      const parentTradeMatch = matches.filter(
        (m) => m.key === match.tradeKey
      )[0];
      // istanbul ignore else
      if (!parentTradeMatch) {
        matches.push(trades.filter((t) => t.key === match.tradeKey)[0]);
      }
    }
  });
  return matches.map(
    ({ name, icon, description, sortOrder, synonyms, shortName, ...rest }) =>
      rest
  );
};

export const findValidFilters = async (
  tradesQuery: Array<string>,
  sectorsQuery: Array<string>,
  typesQuery: Array<string>,
  classificationsQuery: Array<string>,
  locale?: string,
  projectsSearch?: boolean
) => {
  const filtersLists = await getFiltersLists(locale, { projectsSearch });
  let trade = [];
  trade = projectsSearch
    ? (() => {
        const matchedTrades = filtersLists.trades.filter((t) =>
          tradesQuery.some((query) =>
            t.urlKey.toLowerCase().includes(query.toLowerCase())
          )
        );
        const result = [];
        matchedTrades.forEach((t) => {
          result.push(t);
          /* istanbul ignore next: nullish coalescing branch for trade without specialties */
          (t.specialties ?? []).forEach((s) => result.push(s));
        });
        return result;
      })()
    : await findTrades(filtersLists.trades, tradesQuery);
  const constructionSector = findMatches(
    filtersLists.constructionSectors,
    sectorsQuery
  );
  const companyType = findMatches(
    filtersLists.companyTypes,
    typesQuery?.map((s) => s.toLowerCase())
  );

  const classifications = findMatches(
    filtersLists.classifications,
    classificationsQuery
  );

  return {
    trade,
    constructionSector,
    companyType,
    classifications,
  };
};

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  const { trades, sectors, types, classifications, projectsSearch } = req.query;
  const validFilters = await findValidFilters(
    (trades as string)?.split(',') ?? [],
    (sectors as string)?.split(',') ?? [],
    (types as string)?.split(',') ?? [],
    (classifications as string)?.split(',') ?? [],
    undefined,
    projectsSearch === 'true' || projectsSearch === '1'
  );
  return res.status(200).json(validFilters);
}
